import type { ReactNode } from 'react';

import { cn } from '@crossnokaye/ux-common';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@crossnokaye/ux-common/ckds';

import { DropdownButton } from '@/ux-common/components/dropdown-button/dropdown-button';

export type DropdownCellOption = {
  value: string;
  label: string;
};

interface DropdownCellProps {
  value: string | null;
  options: DropdownCellOption[];
  onChange: (value: string) => void;
  onClear?: () => void;
  placeholder?: string;
  disabled?: boolean;
}

/**
 * Dropdown cell renderer.
 *
 * Renders a DropdownButton that fills the cell (full width/height, no rounded
 * corners, transparent background, no uppercase/bold) so it blends with the
 * surrounding row.
 */
export const DropdownCell = ({
  value,
  options,
  onChange,
  onClear,
  placeholder,
  disabled,
}: DropdownCellProps): ReactNode => {
  const selected = value === null ? null : (options.find((o) => o.value === value) ?? null);
  const showClear = onClear !== undefined && value !== null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <DropdownButton
          className={cn(
            'h-full w-full rounded-none bg-transparent normal-case font-normal',
            selected ? 'text-foreground' : 'text-muted-foreground'
          )}
        >
          {selected?.label ?? placeholder ?? ''}
        </DropdownButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        withPortal
        className="!z-[calc(var(--z-modal)+1)] max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-y-auto"
        align="start"
        collisionPadding={8}
      >
        {showClear && (
          <>
            <DropdownMenuItem onSelect={() => onClear?.()}>Clear selection</DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        {options.length === 0 ? (
          <DropdownMenuItem disabled>No options available</DropdownMenuItem>
        ) : (
          options.map((option) => (
            <DropdownMenuItem key={option.value} onSelect={() => onChange(option.value)}>
              {option.label}
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

DropdownCell.displayName = 'DataTable.DropdownCell';
